import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FolderKanban, 
  FileText, 
  Package, 
  Lightbulb,
  ArrowRight,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const RELEASE_PAGE_SIZE = 5;
const RELEASE_STATUS_ORDER = { doing: 0, planned: 1, done: 2 };
const RELEASE_STATUS_META = {
  planned: { label: 'Planned', icon: '📋', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  doing: { label: 'Doing', icon: '🚧', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  done: { label: 'Done', icon: '✅', className: 'bg-green-50 text-green-700 border-green-200' },
};

export const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [myStories, setMyStories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [releases, setReleases] = useState([]);
  const [releasePage, setReleasePage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, myStoriesRes, releasesRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/user-stories/my-stories`, { params: { status: 'not-done' } }),
        axios.get(`${API}/releases`)
      ]);

      setAnalytics(analyticsRes.data);
      setMyStories(myStoriesRes.data.slice(0, 10)); // Show max 10
      const sortedReleases = [...releasesRes.data].sort((a, b) => {
        const orderDiff = (RELEASE_STATUS_ORDER[a.status] ?? 3) - (RELEASE_STATUS_ORDER[b.status] ?? 3);
        return orderDiff !== 0 ? orderDiff : b.created_at.localeCompare(a.created_at);
      });
      setReleases(sortedReleases);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = statuses.find(s => s.key === status);
    if (statusInfo) {
      return (
        <Badge 
          variant="secondary"
          style={{ backgroundColor: statusInfo.color + '20', color: statusInfo.color }}
        >
          {statusInfo.name}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        {status}
      </Badge>
    );
  };

  const statCards = analytics ? [
    { label: 'Projects', value: analytics.counts.projects, icon: FolderKanban, href: '/projects', color: 'text-blue-600' },
    { label: 'User Stories', value: analytics.counts.user_stories, icon: FileText, href: '/user-stories', color: 'text-purple-600' },
    { label: 'Releases', value: analytics.counts.releases, icon: Package, href: '/releases', color: 'text-green-600' },
    { label: 'Ideas', value: analytics.counts.ideas, icon: Lightbulb, href: '/ideas', color: 'text-orange-600' }
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-semibold">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Welcome to your project management hub.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.href}>
              <Card className="card-hover cursor-pointer" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                    <span className="font-semibold tabular-nums text-2xl">{stat.value}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{stat.label}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-1">
        {/* My Assigned Stories */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <CardTitle className="text-xl">My Open Tasks</CardTitle>
            </div>
            <Link to="/user-stories">
              <Button variant="ghost" size="sm" className="gap-2">
                View all <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {myStories.length === 0 ? (
              <div className="empty-state py-8">
                <FileText className="empty-state-icon h-12 w-12" />
                <p className="empty-state-title">No open tasks</p>
                <p className="empty-state-description">You don&apos;t have any assigned open or in-progress stories.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myStories.map((story) => (
                  <Link 
                    key={story.id} 
                    to={`/user-stories/${story.id}`}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                    data-testid={`my-story-${story.issue_number}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="issue-number">{story.issue_number}</span>
                      <div>
                        <p className="font-medium text-sm">{story.title}</p>
                        <p className="text-xs text-muted-foreground">{story.project_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(story.status)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-6">
        {/* Status Overview */}
        <Card className="flex-1 min-w-[320px]">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <CardTitle className="text-xl">Story Status Overview</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {analytics?.stories_by_status?.length === 0 ? (
              <div className="empty-state py-8">
                <FileText className="empty-state-icon h-12 w-12" />
                <p className="empty-state-description">No stories to show</p>
              </div>
            ) : (
              <div className="space-y-4">
                {analytics?.stories_by_status?.map((item) => {
                  const total = analytics.counts.user_stories || 1;
                  const percentage = Math.round((item.count / total) * 100);
                  const statusInfo = statuses.find(s => s.key === item.status);
                  const color = statusInfo?.color || '#6b7280';
                  return (
                    <div key={item.status}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="capitalize font-medium">{statusInfo?.name || item.status}</span>
                        <span className="text-muted-foreground">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all"
                          style={{ width: `${percentage}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Release Status */}
        <Card className="flex-1 min-w-[320px]">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Package className="h-5 w-5 text-green-500" />
              Release Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {releases.length === 0 ? (
              <div className="empty-state py-8">
                <Package className="empty-state-icon h-12 w-12" />
                <p className="empty-state-description">No releases yet</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {releases
                    .slice((releasePage - 1) * RELEASE_PAGE_SIZE, releasePage * RELEASE_PAGE_SIZE)
                    .map((release) => {
                      const meta = RELEASE_STATUS_META[release.status] || RELEASE_STATUS_META.planned;
                      return (
                        <Link
                          key={release.id}
                          to={`/releases/${release.id}`}
                          className="flex items-center justify-between gap-3 p-2.5 rounded-lg border hover:border-primary/50 transition-colors"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{release.project_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">v{release.version}</p>
                          </div>
                          <Badge variant="secondary" className={`gap-1 flex-shrink-0 ${meta.className}`}>
                            <span>{meta.icon}</span>
                            {meta.label}
                          </Badge>
                        </Link>
                      );
                    })}
                </div>
                {releases.length > RELEASE_PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReleasePage((p) => p - 1)}
                      disabled={releasePage === 1}
                      data-testid="release-page-prev"
                    >
                      Previous
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Page {releasePage} of {Math.ceil(releases.length / RELEASE_PAGE_SIZE)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReleasePage((p) => p + 1)}
                      disabled={releasePage >= Math.ceil(releases.length / RELEASE_PAGE_SIZE)}
                      data-testid="release-page-next"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
